import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
export const MAX_STACK_SIZE = 10;
export const StackModalContext = createContext({
    stack: [],
    setStack: () => { },
});
export const StackModalContextProvider = ({ children }) => {
    const [stack, setStack] = useState([]);
    const contextValue = useMemo(() => ({
        stack,
        setStack,
    }), [stack]);
    return _jsx(StackModalContext.Provider, { value: contextValue, children: children });
};
/**
 * Hook for managing a stack of modals
 *
 * @example
 * ```tsx
 * const { push, pop, stack } = useStackModal();
 *
 * const openModal = () => {
 *   push({
 *     id: 'my-modal',
 *     renderContent: (index) => <MyModal index={index} />,
 *     onExit: () => console.log('Modal closed')
 *   });
 * };
 * ```
 */
export function useStackModal() {
    const context = useContext(StackModalContext);
    if (!context) {
        throw new Error('useStackModal must be used within a StackModalContextProvider');
    }
    const { stack, setStack } = context;
    const push = useCallback((item) => {
        if (stack.length >= MAX_STACK_SIZE) {
            throw new Error('Stack size exceeded');
        }
        setStack((prevStack) => prevStack.concat(item));
    }, [setStack]);
    const pop = useCallback((count = 1) => {
        setStack((prevStack) => {
            if (prevStack.length === 0) {
                return prevStack;
            }
            return prevStack.slice(0, Math.max(prevStack.length - count));
        });
    }, [setStack]);
    const clear = useCallback(() => {
        setStack([]);
    }, [setStack]);
    return {
        stack,
        push,
        pop,
        clear,
    };
}
